import { useState } from "react";
import { ArrowLeft, MoreVertical, Mail, CheckCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { MatesGrid } from "@/components/mates/MatesGrid";
import { InviteDialog } from "@/components/mates/InviteDialog";
import { EmptyState } from "@/components/mates/EmptyState";
import { PendingInvites } from "@/components/mates/PendingInvites";
import { ReceivedInvites } from "@/components/mates/ReceivedInvites";
import { useMatesWithPending, useMarkAllRead } from "@/hooks/useMates";
import { useHeartbeat } from "@/hooks/useHeartbeat";
import PageTitleScript from "@/components/MetaScript";
import { CustomLoader } from "@/components/ui/CustomLoader";
import { CustomNoData } from "@/components/ui/CustomNoData";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

export function MatesPage() {
  const navigate = useNavigate();
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const { data: matesData, isLoading, error } = useMatesWithPending();
  const markAllReadMutation = useMarkAllRead();
  const queryClient = useQueryClient();

  // Use heartbeat hook for activity tracking
  useHeartbeat();

  const handleMarkAllRead = async () => {
    try {
      await markAllReadMutation.mutateAsync();
      // 标记已读后重新获取伙伴数据，确保UI状态同步
      // Reload mates data after marking as read to ensure UI state sync
      queryClient.refetchQueries({ queryKey: ["mates", "with-pending"] });
      setDropdownOpen(false);
    } catch (error) {
      console.error("Failed to mark all as read:", error);
      setDropdownOpen(false);
    }
  };

  const handleInviteClick = () => {
    setShowInviteDialog(true);
    setDropdownOpen(false);
  };

  console.log("matesData", matesData);
  const mates = matesData?.mates || [];
  const pendingInvites = matesData?.pending_invites || [];
  const receivedInvites = matesData?.received_invites || [];
  const hasUnreadMates = mates.some((mate) => mate.has_unread);

  if (isLoading) {
    return (
      <div className="flex flex-col p-6 min-h-[60vh]">
        <PageTitleScript title="Workout Mates" />
        <div className="flex items-center mb-6">
          <button onClick={() => navigate(-1)} className="mr-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold text-tx-dark">Mates</h1>
        </div>

        <CustomLoader />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col p-6 min-h-[60vh]">
        <PageTitleScript title="Workout Mates" />
        <h1 className="text-2xl font-bold mb-6 text-tx-dark">Workout Mates</h1>
        <CustomNoData msg="Failed to load mates. Please try again." />
      </div>
    );
  }

  return (
    <div className="flex flex-col p-6">
      <PageTitleScript title="Workout Mates" />
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center">
          <button onClick={() => navigate(-1)} className="mr-2">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-2xl font-bold text-tx-dark">Mates</h1>
        </div>

        <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
          <DropdownMenuTrigger asChild>
            <Button variant="secondary" size="sm" className="h-8 w-8 p-0">
              <MoreVertical className="h-4 w-4" />
              <span className="sr-only">Open menu</span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem
              onClick={handleInviteClick}
              className="cursor-pointer"
            >
              <Mail className="h-4 w-4" />
              Invite Mates
            </DropdownMenuItem>
            {hasUnreadMates && (
              <DropdownMenuItem
                onClick={handleMarkAllRead}
                disabled={markAllReadMutation.isPending}
                className="cursor-pointer"
              >
                <CheckCheck className="h-4 w-4" />
                Mute Notifications
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Received Invites */}
      <ReceivedInvites invites={receivedInvites} />

      {/* Content */}
      {mates.length > 0 && <MatesGrid mates={mates} />}

      {/* Pending Invites */}
      <PendingInvites invites={pendingInvites} />

      {mates.length < 1 &&
        pendingInvites.length < 1 &&
        receivedInvites.length < 1 && (
          <EmptyState onInviteClick={() => setShowInviteDialog(true)} />
        )}

      {/* Invite Dialog */}
      <InviteDialog
        open={showInviteDialog}
        onOpenChange={setShowInviteDialog}
      />
    </div>
  );
}
